import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Check, Lightbulb, Loader2, Sparkles, Wand2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { aiRecipeImage } from '@/services/ai-recipe-image'
import { aiRecipeParser, type AIRecipeIdea } from '@/services/ai-recipe-parser'
import { useHouseholdStore } from '@/stores/household-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { useSettingsStore } from '@/stores/settings-store'
import type { Recipe } from '@/types/recipe'
import { ParsedRecipePreview } from './parsed-recipe-preview'

type ExpandedRecipeState = {
  id: string
  sourceIdea: AIRecipeIdea
  recipe: Partial<Recipe>
  generateImageOnSave: boolean
  isSaving: boolean
  isRefining: boolean
}

const SUGGESTION_PROMPTS = [
  'Ik zoek snelle doordeweekse diners met veel groenten.',
  'Bedenk gezellige comfort food recepten voor een regenachtige dag.',
  'Ik wil lichte lente-recepten met veel kleur en weinig gedoe.',
]

export function RecipeInspiration() {
  const [prompt, setPrompt] = useState('')
  const [ideas, setIdeas] = useState<AIRecipeIdea[]>([])
  const [selectedIdeaIds, setSelectedIdeaIds] = useState<string[]>([])
  const [expandedRecipes, setExpandedRecipes] = useState<ExpandedRecipeState[]>([])
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false)
  const [isExpandingIdeas, setIsExpandingIdeas] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [platformAIConfigured, setPlatformAIConfigured] = useState<boolean | null>(aiRecipeParser.peekStatus())
  const [platformAIImageConfigured, setPlatformAIImageConfigured] = useState<boolean | null>(aiRecipeImage.peekStatus())

  const { createRecipe } = useRecipeStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const { aiEnabled } = useSettingsStore()
  const { toast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    void aiRecipeParser.getStatus().then(setPlatformAIConfigured)
    void aiRecipeImage.getStatus().then(setPlatformAIImageConfigured)
  }, [])

  const isAIAvailable = aiEnabled && platformAIConfigured !== false
  const selectedIdeas = useMemo(
    () => ideas.filter((idea) => selectedIdeaIds.includes(idea.id)),
    [ideas, selectedIdeaIds]
  )

  const toggleIdea = (ideaId: string) => {
    setSelectedIdeaIds((current) =>
      current.includes(ideaId) ? current.filter((id) => id !== ideaId) : [...current, ideaId]
    )
  }

  const handleGenerateIdeas = async () => {
    if (!prompt.trim()) {
      setError('Beschrijf eerst waar je zin in hebt of wat voor soort inspiratie je zoekt.')
      return
    }

    setIsGeneratingIdeas(true)
    setError(null)
    setIdeas([])
    setSelectedIdeaIds([])
    setExpandedRecipes([])

    const result = await aiRecipeParser.generateRecipeIdeas(prompt)
    setIsGeneratingIdeas(false)

    if (!result.success || !result.ideas) {
      setError(result.error || 'Het lukte niet om inspiratie op te halen.')
      return
    }

    setIdeas(result.ideas)
  }

  const handleExpandIdeas = async () => {
    if (!selectedIdeas.length) {
      setError('Kies eerst minstens één idee om uit te werken.')
      return
    }

    setIsExpandingIdeas(true)
    setError(null)
    const nextExpandedRecipes: ExpandedRecipeState[] = []

    for (const idea of selectedIdeas) {
      const result = await aiRecipeParser.expandRecipeIdea(
        {
          title: idea.title,
          description: idea.description,
          why_it_fits: idea.why_it_fits,
          prep_time: idea.prep_time,
          cook_time: idea.cook_time,
          servings: idea.servings,
          difficulty: idea.difficulty,
          meal_type: idea.meal_type,
          tags: idea.tags,
        },
        prompt
      )

      if (result.success && result.recipe) {
        nextExpandedRecipes.push({
          id: idea.id,
          sourceIdea: idea,
          recipe: result.recipe,
          generateImageOnSave: false,
          isSaving: false,
          isRefining: false,
        })
      }
    }

    setIsExpandingIdeas(false)

    if (!nextExpandedRecipes.length) {
      setError('Geen van de gekozen ideeën kon goed worden uitgewerkt. Probeer een andere wens of kies andere ideeën.')
      return
    }

    setExpandedRecipes(nextExpandedRecipes)
  }

  const updateExpandedRecipe = (recipeId: string, updater: (state: ExpandedRecipeState) => ExpandedRecipeState) => {
    setExpandedRecipes((current) => current.map((item) => (item.id === recipeId ? updater(item) : item)))
  }

  const handleSaveRecipe = async (recipeId: string) => {
    const currentRecipe = expandedRecipes.find((item) => item.id === recipeId)
    if (!currentRecipe?.recipe.title || !currentRecipe.recipe.ingredients || !currentRecipe.recipe.instructions) {
      return
    }

    updateExpandedRecipe(recipeId, (item) => ({ ...item, isSaving: true }))
    let recipeToSave = currentRecipe.recipe

    if (currentRecipe.generateImageOnSave && !recipeToSave.image_url && platformAIImageConfigured !== false) {
      const imageResult = await aiRecipeImage.generateRecipeImage(recipeToSave)
      if (imageResult.success && imageResult.imageUrl) {
        recipeToSave = { ...recipeToSave, image_url: imageResult.imageUrl }
        updateExpandedRecipe(recipeId, (item) => ({ ...item, recipe: recipeToSave }))
      } else {
        toast({
          title: 'Recept opgeslagen zonder foto',
          description: imageResult.error || 'De app kon geen passende afbeelding maken.',
          variant: 'destructive',
        })
      }
    }

    const createdRecipe = await createRecipe(
      {
        title: recipeToSave.title || '',
        description: recipeToSave.description || '',
        ingredients: recipeToSave.ingredients || [],
        instructions: recipeToSave.instructions || [],
        prep_time: recipeToSave.prep_time || null,
        cook_time: recipeToSave.cook_time || null,
        servings: recipeToSave.servings || null,
        difficulty: recipeToSave.difficulty || '',
        meal_type: recipeToSave.meal_type || '',
        tags: recipeToSave.tags || [],
        equipment: recipeToSave.equipment || [],
        image_url: recipeToSave.image_url || '',
        rating: recipeToSave.rating || null,
        notes: recipeToSave.notes || '',
      },
      viewMode === 'household' ? currentHousehold?.id : undefined
    )

    updateExpandedRecipe(recipeId, (item) => ({ ...item, isSaving: false }))

    if (createdRecipe) {
      navigate(`/recepten/${createdRecipe.id}`)
    }
  }

  const handleRefineRecipe = async (recipeId: string, instruction: string) => {
    const currentRecipe = expandedRecipes.find((item) => item.id === recipeId)
    if (!currentRecipe) {
      return
    }

    updateExpandedRecipe(recipeId, (item) => ({ ...item, isRefining: true }))
    const result = await aiRecipeParser.refineRecipe(currentRecipe.recipe, instruction)
    updateExpandedRecipe(recipeId, (item) => ({ ...item, isRefining: false }))

    if (!result.success || !result.recipe) {
      toast({
        title: 'Aanpassen lukte niet',
        description: result.error || 'De app kon deze wijziging niet goed verwerken.',
        variant: 'destructive',
      })
      return
    }

    const refinedRecipe = result.recipe

    updateExpandedRecipe(recipeId, (item) => ({
      ...item,
      recipe: {
        ...item.recipe,
        ...refinedRecipe,
        image_url: item.recipe.image_url || refinedRecipe.image_url,
      },
    }))
  }

  if (!isAIAvailable) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Receptinspiratie
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Deze hulp is nu niet beschikbaar</h3>
            <p className="text-gray-600">
              De beheerder moet de slimme functies eerst inschakelen voordat de app receptideeën kan voorstellen.
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Receptinspiratie met slimme hulp
            <Badge variant="secondary" className="ml-2">
              Inspiratie
            </Badge>
          </CardTitle>
          <p className="text-sm text-gray-600">
            Vertel waar je zin in hebt. De app bedenkt 5 verschillende ideeën. Kies daarna één of meerdere ideeën om uit te werken tot echte recepten.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Waar heb je zin in?</label>
            <Textarea
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={4}
              placeholder="Bijvoorbeeld: Ik wil doordeweekse lentegerechten met veel groenten, niet te pittig en het liefst klaar in 30 minuten."
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {SUGGESTION_PROMPTS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setPrompt(suggestion)}
                className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-left text-xs text-gray-700 transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
              >
                {suggestion}
              </button>
            ))}
          </div>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => void handleGenerateIdeas()} disabled={isGeneratingIdeas || !prompt.trim()} className="flex-1">
              {isGeneratingIdeas ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ideeën bedenken...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Geef me 5 ideeën
                </>
              )}
            </Button>

            {(ideas.length > 0 || expandedRecipes.length > 0 || prompt) ? (
              <Button
                variant="outline"
                onClick={() => {
                  setPrompt('')
                  setIdeas([])
                  setSelectedIdeaIds([])
                  setExpandedRecipes([])
                  setError(null)
                }}
              >
                Opnieuw
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {ideas.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Kies ideeën om uit te werken</CardTitle>
            <p className="text-sm text-gray-600">
              Selecteer één of meer ideeën. Daarna maakt de app er echte recepten van met ingrediënten en stappen.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {ideas.map((idea) => {
                const isSelected = selectedIdeaIds.includes(idea.id)
                const totalTime = (idea.prep_time || 0) + (idea.cook_time || 0)

                return (
                  <button
                    key={idea.id}
                    type="button"
                    onClick={() => toggleIdea(idea.id)}
                    className={`rounded-xl border p-4 text-left transition ${
                      isSelected
                        ? 'border-orange-400 bg-orange-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-orange-200 hover:shadow-sm'
                    }`}
                  >
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{idea.title}</h3>
                        <p className="mt-1 text-sm text-gray-600">{idea.description}</p>
                      </div>
                      <span
                        className={`mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border ${
                          isSelected ? 'border-orange-500 bg-orange-500 text-white' : 'border-gray-300 text-transparent'
                        }`}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </span>
                    </div>

                    {idea.why_it_fits ? (
                      <p className="mb-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">{idea.why_it_fits}</p>
                    ) : null}

                    <div className="mb-3 flex flex-wrap gap-3 text-xs text-gray-500">
                      {totalTime > 0 ? <span>{totalTime} min</span> : null}
                      {idea.servings ? <span>{idea.servings} porties</span> : null}
                      {idea.difficulty ? <span className="capitalize">{idea.difficulty}</span> : null}
                    </div>

                    {idea.tags?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {idea.tags.slice(0, 4).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-600">
                {selectedIdeas.length === 0
                  ? 'Nog niets geselecteerd'
                  : `${selectedIdeas.length} ${selectedIdeas.length === 1 ? 'idee geselecteerd' : 'ideeën geselecteerd'}`}
              </p>
              <Button onClick={() => void handleExpandIdeas()} disabled={isExpandingIdeas || selectedIdeas.length === 0}>
                {isExpandingIdeas ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Recepten uitwerken...
                  </>
                ) : (
                  <>
                    <Wand2 className="mr-2 h-4 w-4" />
                    Werk selectie uit
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {expandedRecipes.length > 0 ? (
        <div className="space-y-6">
          {expandedRecipes.map((item) => (
            <div key={item.id}>
              <div className="mb-3">
                <p className="text-sm font-medium text-orange-700">Uitgewerkt idee: {item.sourceIdea.title}</p>
              </div>
              <ParsedRecipePreview
                parsedRecipe={item.recipe}
                onSave={() => handleSaveRecipe(item.id)}
                onRefineRecipe={(instruction) => handleRefineRecipe(item.id, instruction)}
                isRefiningRecipe={item.isRefining}
                generateImageOnSave={item.generateImageOnSave}
                onGenerateImageOnSaveChange={(enabled) =>
                  updateExpandedRecipe(item.id, (current) => ({ ...current, generateImageOnSave: enabled }))
                }
                aiImageAvailable={platformAIImageConfigured !== false}
                isSaving={item.isSaving}
                onImageChange={(imageUrl) =>
                  updateExpandedRecipe(item.id, (current) => ({
                    ...current,
                    recipe: { ...current.recipe, image_url: imageUrl },
                  }))
                }
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
